import { Button } from '@/components/ui/button';
import { SaveButton } from '@/components/ui/SaveButton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ArrowLeft, Save, Globe, EyeOff } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ActivityComposerFooterProps {
  mode: 'new' | 'edit';
  onCancel: () => void;
  onSaveDraft: () => void;
  onSaveChanges?: () => void;
  onPublish?: () => void;
  onUnpublish?: () => void;
  isSaving: boolean;
  isDirty: boolean;
  canSaveDraft: boolean;
  saveDraftHint?: string;
  isPublished?: boolean;
  publishAllowed?: boolean;
  publishBlockReason?: string;
  className?: string;
}

export function ActivityComposerFooter({
  mode,
  onCancel,
  onSaveDraft,
  onSaveChanges,
  onPublish,
  onUnpublish,
  isSaving,
  isDirty,
  canSaveDraft,
  saveDraftHint,
  isPublished = false,
  publishAllowed = false,
  publishBlockReason,
  className,
}: ActivityComposerFooterProps) {
  const publishDisabled = isSaving || !publishAllowed || (isPublished && !isDirty);

  return (
    <div
      className={cn(
        'fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80',
        'px-4 py-3',
        'pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))]',
        'shadow-[0_-4px_12px_rgba(0,0,0,0.08)]',
        'lg:hidden', // hidden on desktop — top actions replace it
        className,
      )}
    >
      {/* Hint row */}
      {saveDraftHint && !canSaveDraft && (
        <p className="text-[11px] text-muted-foreground mb-2">{saveDraftHint}</p>
      )}

      {/* Publish block hint - visible when publish is disabled and there's a reason */}
      {publishDisabled && publishBlockReason && !isPublished && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30 p-3 mb-3" data-testid="publish-block-hint">
          <p className="text-[12px] text-amber-800 dark:text-amber-200 font-medium" data-testid="publish-block-reason">
            {publishBlockReason}
          </p>
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        {/* Left: Cancel / Back */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onCancel}
          disabled={isSaving}
          className="h-9 text-xs"
          data-testid="event-cancel"
        >
          <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />
          {mode === 'new' ? 'Avbryt' : 'Tillbaka'}
        </Button>

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          {/* Publish / Unpublish (edit mode only) */}
          {mode === 'edit' && isPublished && onUnpublish && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onUnpublish}
              disabled={isSaving}
              className="h-9 text-xs text-destructive hover:text-destructive"
              data-testid="event-unpublish"
            >
              <EyeOff className="h-3.5 w-3.5 mr-1.5" />
              Avpublicera
            </Button>
          )}

          {mode === 'edit' && !isPublished && onPublish && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => onPublish?.()}
                      disabled={publishDisabled}
                      className="h-9 text-xs border-primary text-primary hover:bg-primary/10"
                      data-testid="event-publish-btn"
                    >
                      <Globe className="h-3.5 w-3.5 mr-1.5" />
                      Publicera
                    </Button>
                  </span>
                </TooltipTrigger>
                {publishDisabled && publishBlockReason && (
                  <TooltipContent side="top" className="text-xs max-w-[220px]">
                    {publishBlockReason}
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          )}

          {/* Primary save action */}
          {mode === 'new' ? (
            <SaveButton
              type="button"
              size="sm"
              onClick={() => onSaveDraft()}
              disabled={!canSaveDraft}
              isSaving={isSaving}
              savingLabel="Sparar…"
              className="h-9 text-xs"
              data-testid="event-save-draft"
            >
              <Save className="h-3.5 w-3.5 mr-1.5" />
              Spara utkast
            </SaveButton>
          ) : (
            <SaveButton
              type="button"
              size="sm"
              onClick={() => (onSaveChanges ?? onSaveDraft)()}
              disabled={isSaving || !isDirty}
              isSaving={isSaving}
              savingLabel="Sparar…"
              className="h-9 text-xs"
              data-testid="event-save-changes"
            >
              <Save className="h-3.5 w-3.5 mr-1.5" />
              Spara
            </SaveButton>
          )}
        </div>
      </div>
    </div>
  );
}
